/**
 * Shared AI trigger button.
 *
 * Unifies color / hover / disabled / re-enrich (warning) states.
 * Modules keep their own radius and font via `$compact` / `$iconOnly`
 * or by wrapping: `styled(AIButton)\`border-radius: 4px;\``.
 */
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { AI_COLORS } from '../config/brandColors';

const AI_RGB = '124, 58, 237'; // #7c3aed

const warningCss = css`
  background: rgba(245, 158, 11, 0.2);
  border: 1px solid rgba(245, 158, 11, 0.5);
  color: #fbbf24;
  &:hover:not(:disabled) {
    background: rgba(245, 158, 11, 0.3);
    border-color: #f59e0b;
    color: #fff;
  }
`;

const solidCss = css`
  background: linear-gradient(135deg, ${AI_COLORS.base} 0%, ${AI_COLORS.deep} 100%);
  border: 1px solid transparent;
  color: #fff;
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, ${AI_COLORS.light} 0%, ${AI_COLORS.base} 100%);
    box-shadow: 0 4px 16px rgba(${AI_RGB}, 0.3);
    transform: translateY(-1px);
  }

  html[data-platform='tizen'] & {
    background: ${AI_COLORS.base};
  }

  html[data-platform='tizen'] &:hover:not(:disabled) {
    background: ${AI_COLORS.light};
  }
`;

const ghostCss = css`
  background: rgba(${AI_RGB}, 0.12);
  border: 1px solid rgba(${AI_RGB}, 0.4);
  color: ${AI_COLORS.text};
  &:hover:not(:disabled) {
    background: rgba(${AI_RGB}, 0.25);
    border-color: ${AI_COLORS.base};
    color: #fff;
  }
`;

const AIButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${p => (p.$iconOnly ? '0' : '6px')};
  cursor: pointer;
  transition: all 0.15s ease;
  font-weight: 600;
  font-family: inherit;
  line-height: 1.2;
  border-radius: ${p => (p.$compact ? '6px' : '8px')};
  font-size: ${p => {
    if (p.$iconOnly) return 'inherit';
    if (p.$compact) return '0.75rem';
    return '0.85rem';
  }};
  padding: ${p => {
    if (p.$iconOnly) return '0';
    if (p.$compact) return '4px 10px';
    return '8px 16px';
  }};
  width: ${p => (p.$iconOnly ? '32px' : 'auto')};
  height: ${p => (p.$iconOnly ? '32px' : 'auto')};
  flex-shrink: 0;

  ${p => (p.$warning ? warningCss : p.$variant === 'solid' ? solidCss : ghostCss)}

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

AIButton.defaultProps = {
  $variant: 'ghost',
  type: 'button',
};

export default AIButton;
export { AI_COLORS };
